/* ============================================================================
   SL UPLOAD HANDOFF  —  carrying ONE selected file across a navigation

   The customer picks artwork on the home page and the site navigates into the
   builder. Navigation destroys every File object on the page, so the selection
   has to be parked somewhere that survives it.

   ── DEFECT 1 (D-098): base64 in sessionStorage ───────────────────────────
   Home parked a base64 data URL in sessionStorage inside an empty catch. Quota
   is counted in UTF-16 units, so each base64 character costs TWO bytes: a
   2,535,968-byte photo asked for 6.45 MB against a ~5 MB quota, `setItem`
   threw, the catch swallowed it, and the page navigated anyway. Ceiling: about
   1.88 MB. Every phone photo failed; logos did not, which hid it.

   ── DEFECT 2 (D-100): the IndexedDB write itself failed on a real iPhone ──
   The replacement stored the File object directly. On a real iPhone that write
   REJECTED — Home showed "We couldn't carry that file into the builder" and
   never navigated, so the builder was never even reached.

   WebKit is the reason to suspect the File: structured-cloning a File that is
   backed by the iOS photo library has a long history of throwing DataCloneError
   where Chrome clones it happily. That is a hypothesis, not a proven fact — the
   device is not automatable from here — so this module no longer BETS on any
   single representation:

       1. the File itself          — richest; keeps name/type/lastModified
       2. a Blob copy of its bytes — plain Blob, the safest thing WebKit clones
       3. an ArrayBuffer           — universally structured-cloneable

   Each is tried in its own transaction, because a failed clone aborts the one
   it was attempted in. Whichever succeeds is recorded, so the next real-device
   run REPORTS which representation the device accepted instead of guessing.

   Every stage is named (`stage` on both success and failure) so a failure says
   WHICH step broke — open, upgrade, transaction, clone, commit — rather than
   collapsing into one generic message.

   Still true, and still the point: no base64, no data URL, no object URL, and
   the file becomes durable only when Add to Cart attaches `_artwork_original`.
   ============================================================================ */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SLUploadHandoff = factory(); }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var DB_NAME = 'stickrlab';
  var DB_VERSION = 1;
  var STORE = 'handoff';
  var KEY = 'pending';

  /* Long enough to survive a slow phone finishing a navigation; short enough
     that a flow abandoned this morning cannot appear on tonight's design. */
  var MAX_AGE_MS = 30 * 60 * 1000;

  /* A transaction that never fires an event is a hang, and a hang on the home
     page looks like a dead button. iOS has been seen to do this when storage is
     under pressure, so every transaction is bounded. */
  var TX_TIMEOUT_MS = 8000;

  /* Reading the selected file's bytes on Home is bounded too (P0, 27 Oct): a
     read that has not finished by then gives way to the File/Blob paths below. */
  var OWN_READ_TIMEOUT_MS = 10000;

  function err(stage, e) {
    var name = (e && e.name) ? e.name : '';
    var msg = (e && e.message) ? e.message : String(e || '');
    var out = new Error(stage + (name ? (': ' + name) : '') + (msg ? (' — ' + msg) : ''));
    out.stage = stage;
    out.cause = name || null;
    return out;
  }

  /**
   * Is a stored record still allowed to be used?
   * Pure, so the rule is testable without a browser.
   */
  function isFresh(rec, nowMs) {
    if (!rec || typeof rec !== 'object') return false;
    if (!rec.blob && !rec.bytes) return false;
    var ts = +rec.ts;
    if (!isFinite(ts) || ts <= 0) return false;
    var age = nowMs - ts;
    if (age < -60000) return false;          // a large forward stamp is not freshness
    return age <= MAX_AGE_MS;
  }

  function idb() {
    return (typeof indexedDB !== 'undefined') ? indexedDB
      : (typeof window !== 'undefined' && window.indexedDB) ? window.indexedDB : null;
  }

  function open() {
    return new Promise(function (resolve, reject) {
      var factory = idb();
      if (!factory) { reject(err('indexeddb_unavailable')); return; }
      var req;
      try { req = factory.open(DB_NAME, DB_VERSION); }
      catch (e) { reject(err('db_open_threw', e)); return; }
      req.onupgradeneeded = function () {
        try {
          var db = req.result;
          if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
        } catch (e) { reject(err('store_create_failed', e)); }
      };
      req.onsuccess = function () {
        var db = req.result;
        /* A database that exists WITHOUT our store cannot be used, and saying
           so by name beats a NotFoundError from the transaction below. */
        if (!db.objectStoreNames.contains(STORE)) {
          db.close();
          reject(err('store_missing'));
          return;
        }
        resolve(db);
      };
      req.onerror = function () { reject(err('db_open_failed', req.error)); };
      req.onblocked = function () { reject(err('db_blocked')); };
    });
  }

  /**
   * Run one transaction and resolve ONLY on `oncomplete`.
   *
   * `put().onsuccess` fires before the data is committed. Navigating on it can
   * race page teardown and lose the write — so navigation waits for THIS.
   */
  function tx(mode, stage, run) {
    return open().then(function (db) {
      return new Promise(function (resolve, reject) {
        var t, store, out, settled = false;
        var done = function (fn, arg) { if (settled) return; settled = true; try { db.close(); } catch (e) {} fn(arg); };
        var timer = setTimeout(function () {
          try { t && t.abort(); } catch (e) {}
          done(reject, err(stage + '_timeout'));
        }, TX_TIMEOUT_MS);

        try { t = db.transaction(STORE, mode); store = t.objectStore(STORE); }
        catch (e) { clearTimeout(timer); done(reject, err(stage + '_transaction_failed', e)); return; }

        try { out = run(store); }
        catch (e) { clearTimeout(timer); done(reject, err(stage + '_write_failed', e)); return; }

        /* COMMITTED, not merely requested. */
        t.oncomplete = function () { clearTimeout(timer); done(resolve, out && out.result !== undefined ? out.result : out); };
        t.onerror = function () { clearTimeout(timer); done(reject, err(stage + '_tx_error', t.error)); };
        t.onabort = function () { clearTimeout(timer); done(reject, err(stage + '_tx_abort', t.error)); };
      });
    });
  }

  function baseRecord(file, now) {
    return {
      name: (file && file.name) || 'artwork',
      type: (file && file.type) || '',
      size: (file && typeof file.size === 'number') ? file.size : 0,
      lastModified: (file && file.lastModified) || null,
      ts: now
    };
  }

  /**
   * Park the customer's selection. REPLACES anything already pending.
   *
   * Tries three representations in order and resolves with the one that
   * committed, so the caller can report what this device actually accepted.
   *
   * @returns {Promise<Object>} the stored descriptor, with `.stored` naming the
   *          representation and `.stage` the step that succeeded.
   */
  function put(file, opts) {
    var o = opts || {};
    if (!file) return Promise.reject(err('no_file'));
    var now = o.now || Date.now();

    var attempt = function (stage, build) {
      return Promise.resolve()
        .then(build)
        .then(function (rec) {
          return tx('readwrite', stage, function (store) { store.put(rec, KEY); return rec; })
            .then(function () { rec.stored = stage; return rec; });
        });
    };

    /* ── DEFECT 3 (P0, 27 Oct 2026): the builder could not READ what Home stored ──
       Ryan's real iPhone, from Home: the builder opened, the peek found the
       record, `file_selected` fired, and then nothing until "upload stalled"
       at 25 s. Between those two stages the builder only reads the file's
       bytes. A stored File (or a Blob copy of it, which WebKit creates lazily
       from the same backing file) still refers to the photo the picker handed
       THE HOME PAGE; after the navigation that reference is not something the
       builder's page can be relied on to read. So the bytes are now read HERE,
       while Home still owns the file, and the builder receives its own copy:
       the identical original bytes, nothing re-encoded. Only if that read fails
       or does not finish in time do the File and Blob paths below run, exactly
       as before. */
    var ownBytes = function () {
      return new Promise(function (resolve, reject) {
        var settled = false;
        var finish = function (fn, arg) { if (settled) return; settled = true; clearTimeout(timer); fn(arg); };
        var timer = setTimeout(function () { finish(reject, err('own_read_timeout')); }, OWN_READ_TIMEOUT_MS);
        var viaReader = function () {
          try {
            var r = new FileReader();
            r.onload = function () { finish(resolve, r.result); };
            r.onerror = function () { finish(reject, err('own_read_failed', r.error)); };
            r.readAsArrayBuffer(file);
          } catch (e) { finish(reject, err('own_read_failed', e)); }
        };
        if (file.arrayBuffer) file.arrayBuffer().then(function (buf) { finish(resolve, buf); }, viaReader);
        else viaReader();
      }).then(function (buf) {
        if (!buf || buf.byteLength !== file.size) throw err('own_read_short');
        return buf;
      });
    };

    /* 0. the file's own bytes, read on Home (P0) */
    var ownErr = null;
    return attempt('own_bytes', function () {
      return ownBytes().then(function (buf) { var rec = baseRecord(file, now); rec.bytes = buf; return rec; });
    })
    /* 1. the File itself — richest, and what every other browser accepts. */
    .catch(function (e0) { ownErr = e0; return attempt('file', function () {
      var rec = baseRecord(file, now); rec.blob = file; return rec;
    }); })
    /* 2. a plain Blob copy. WebKit clones these reliably even where a
          photo-library-backed File throws DataCloneError. */
    .catch(function (e1) {
      return attempt('blob', function () {
        var rec = baseRecord(file, now);
        rec.blob = new Blob([file], { type: rec.type || 'application/octet-stream' });
        return rec;
      }).catch(function (e2) {
        /* 3. raw bytes. Structured clone cannot refuse an ArrayBuffer; if this
              fails the problem is the database, not the payload. */
        return attempt('bytes', function () {
          var read = file.arrayBuffer ? file.arrayBuffer()
            : new Promise(function (res, rej) {
                var r = new FileReader();
                r.onload = function () { res(r.result); };
                r.onerror = function () { rej(err('filereader_failed', r.error)); };
                r.readAsArrayBuffer(file);
              });
          return read.then(function (buf) {
            var rec = baseRecord(file, now); rec.bytes = buf; return rec;
          });
        }).catch(function (e3) {
          /* Report the FIRST failure — it is the one that describes the device
             — while naming every stage that was tried. */
          var out = err('put_failed_all', e1);
          out.tried = [ownErr && ownErr.stage, e1 && e1.stage, e2 && e2.stage, e3 && e3.stage].filter(Boolean);
          out.lastStage = e3 && e3.stage;
          throw out;
        });
      });
    });
  }

  /**
   * READ WITHOUT DELETING.
   *
   * `take()` used to read and delete in one transaction. That is too
   * aggressive: if the builder then failed to open the file, the pending
   * upload was already gone and there was nothing to retry with. Ownership now
   * transfers only when the canonical upload path has actually accepted it —
   * `consume()` below.
   */
  function peek(opts) {
    var o = opts || {};
    var now = o.now || Date.now();
    return tx('readonly', 'peek', function (store) { return store.get(KEY); })
      .then(function (rec) { return isFresh(rec, now) ? rec : null; });
  }

  /** Ownership has transferred. Drop the pending record. */
  function consume() {
    return tx('readwrite', 'consume', function (store) { store.delete(KEY); })
      .then(function () { return true; });
  }

  /** Drop anything pending — an abandoned flow, or a record we refused. */
  function clear() { return consume(); }

  /**
   * Turn a stored record back into something `readFile` accepts, so the builder
   * feeds the SAME function an in-builder upload uses.
   *
   * A File is preferred because `readFile` reads `.name`; where `new File` is
   * unavailable or refuses, a Blob is given a `name` property instead, which is
   * all the caller actually reads.
   */
  function toFile(rec) {
    if (!rec) return null;
    var name = rec.name || 'artwork';
    var type = rec.type || '';
    var parts = rec.blob ? [rec.blob] : (rec.bytes ? [rec.bytes] : null);
    if (!parts) return null;
    try {
      if (typeof File === 'function') {
        var opts = { type: type };
        if (rec.lastModified) opts.lastModified = rec.lastModified;
        return new File(parts, name, opts);
      }
    } catch (e) { /* older WebKit — fall through */ }
    try {
      var b = (parts[0] instanceof Blob) ? parts[0] : new Blob(parts, { type: type });
      try { Object.defineProperty(b, 'name', { value: name, configurable: true }); }
      catch (e2) { try { b.name = name; } catch (e3) {} }
      return b;
    } catch (e4) { return null; }
  }

  return {
    DB_NAME: DB_NAME, STORE: STORE, KEY: KEY, MAX_AGE_MS: MAX_AGE_MS,
    TX_TIMEOUT_MS: TX_TIMEOUT_MS,
    isFresh: isFresh, put: put, peek: peek, consume: consume, clear: clear,
    toFile: toFile,
    available: function () { return !!idb(); }
  };
});
